import type { StatusIndicatorProps } from '@cloudscape-design/components';
import type { EvaluationSummary } from '@amzn/helianthus-arbiter-client';
import { EvaluationStatus } from '@amzn/helianthus-arbiter-client';

export type EvaluationOutcome = 'PASS' | 'FAIL' | 'ERROR' | 'INDETERMINATE';

const EVALUATION_STATUS_CONFIG: Record<EvaluationStatus, { label: string; type: StatusIndicatorProps.Type }> = {
  [EvaluationStatus.INITIALIZING]: { label: 'Initializing', type: 'pending' },
  [EvaluationStatus.IN_PROGRESS]: { label: 'In progress', type: 'in-progress' },
  [EvaluationStatus.COMPLETED]: { label: 'Completed', type: 'info' },
  [EvaluationStatus.ERROR]: { label: 'Error', type: 'error' },
  [EvaluationStatus.CANCELLED]: { label: 'Cancelled', type: 'stopped' },
};

export const EVALUATION_STATUS_DISPLAY: Record<EvaluationStatus, string> =
  Object.fromEntries(
    Object.entries(EVALUATION_STATUS_CONFIG).map(([k, v]) => [k, v.label]),
  ) as Record<EvaluationStatus, string>;

export const EVALUATION_STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  ...Object.entries(EVALUATION_STATUS_CONFIG).map(([value, { label }]) => ({ label, value })),
];

export function getStatusType(status: EvaluationStatus): StatusIndicatorProps.Type {
  return EVALUATION_STATUS_CONFIG[status]?.type ?? 'info';
}

export function getOutcomeType(outcome: EvaluationOutcome): StatusIndicatorProps.Type {
  switch (outcome) {
    case 'PASS':
      return 'success';
    case 'FAIL':
      return 'error';
    case 'ERROR':
      return 'error';
    case 'INDETERMINATE':
      return 'warning';
    default:
      return 'info';
  }
}

export const UNKNOWN_SUBJECT = 'Unknown subject';

export function formatSubject(evaluation: EvaluationSummary): string {
  if (evaluation.subject?.awsResource) {
    const { resourceType, resourceId } = evaluation.subject.awsResource;
    return `${resourceType}: ${resourceId}`;
  }
  if (evaluation.subject?.code) {
    const { packageName, commitId } = evaluation.subject.code;
    return `${packageName}@${commitId?.slice(0, 8)}`;
  }
  if (evaluation.subject?.codeDiff) {
    const { packageName } = evaluation.subject.codeDiff;
    return packageName || 'Code Diff';
  }
  return UNKNOWN_SUBJECT;
}

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none';

export function getConfidenceLevel(confidence: number | undefined | null): ConfidenceLevel {
  if (confidence == null) return 'none';
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

export function getConfidenceIndicatorType(level: ConfidenceLevel): StatusIndicatorProps.Type {
  switch (level) {
    case 'high': return 'success';
    case 'medium': return 'warning';
    case 'low': return 'error';
    case 'none': return 'stopped';
  }
}

/**
 * Parse a ruleId string (format "ruleName@version") into its components.
 *
 * Edge cases:
 * - No "@" suffix: returns { ruleName: ruleId, version: undefined }
 * - Non-numeric suffix after "@": treats entire string as the name
 * - Empty/null/undefined: returns { ruleName: '', version: undefined }
 */
export function parseRuleId(ruleId: string | null | undefined): { ruleName: string; version: number | undefined } {
  if (!ruleId) return { ruleName: '', version: undefined };

  const atIndex = ruleId.lastIndexOf('@');
  if (atIndex === -1) return { ruleName: ruleId, version: undefined };

  const name = ruleId.substring(0, atIndex);
  const suffix = ruleId.substring(atIndex + 1);
  const parsed = Number(suffix);

  if (!name || !suffix || !Number.isInteger(parsed) || parsed < 0) {
    return { ruleName: ruleId, version: undefined };
  }

  return { ruleName: name, version: parsed };
}

export function buildRuleUrl(ruleName: string, version?: number | string | null): string {
  const base = `/rules/${encodeURIComponent(ruleName)}`;
  return version != null && version !== '' ? `${base}?version=${version}` : base;
}
